import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, post } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageLoader, Badge } from "@/components/common";
import { PlayCircle, FileText, CheckCircle2, Lock, ChevronLeft, Loader2 } from "lucide-react";

export default function CourseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { lang } = useLang();
  const [ctrans, setCtrans] = useState(null);
  const [active, setActive] = useState(null);
  const [enrolled, setEnrolled] = useState(false);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);

  const { data: course, isLoading } = useQuery({ queryKey: ["course", id], queryFn: () => get(`/courses/${id}`) });
  const { data: prog, refetch: refetchProg } = useQuery({ queryKey: ["prog", id], queryFn: () => get(`/progress/${id}`) });
  const { data: enrolls } = useQuery({ queryKey: ["enrolls"], queryFn: () => get("/my/enrollments") });

  useEffect(() => {
    if (enrolls) setEnrolled(enrolls.some((e) => e.item_id === id));
  }, [enrolls, id]);

  useEffect(() => {
    if (course && !active) {
      const first = course.modules?.[0]?.lessons?.[0];
      if (first) setActive(first);
    }
  }, [course, active]);

  useEffect(() => {
    if (lang === "hi" && course && !ctrans) post("/i18n/translate", { entity_type: "course", entity_id: id, target: "hi" }).then(setCtrans).catch(() => {});
  }, [lang, course, ctrans, id]);

  if (isLoading || !course) return <PageLoader />;
  const disp = lang === "hi" && ctrans ? { ...course, ...ctrans } : course;
  const lessons = course.modules.flatMap((m) => m.lessons);
  const canPlay = (l) => enrolled || course.is_free || l.is_preview;
  const progMap = Object.fromEntries((prog?.items || []).map((p) => [p.lesson_id, p]));

  const enroll = async () => {
    setBusy(true);
    try {
      if (course.is_free) { await post("/enroll/free", { item_type: "course", item_id: id }); toast.success("Enrolled!"); setEnrolled(true); }
      else nav(`/app/subscriptions?buy=course&id=${id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const saveProgress = async (completed = false) => {
    if (!active || !videoRef.current) return;
    const v = videoRef.current;
    await post("/progress", { lesson_id: active.id, course_id: id, position_sec: Math.floor(v.currentTime), duration_sec: Math.floor(v.duration || active.duration_sec), completed }).catch(() => {});
    refetchProg();
  };

  const selectLesson = (l) => {
    if (!canPlay(l)) { toast.error("Enroll to unlock this lesson"); return; }
    setActive(l);
  };

  return (
    <div>
      <button onClick={() => nav("/app/courses")} className="text-sm text-slate-500 flex items-center gap-1 mb-4"><ChevronLeft className="h-4 w-4" /> Back to courses</button>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-black rounded-2xl overflow-hidden aspect-video">
            {active && canPlay(active) && active.lesson_type === "video" ? (
              <video ref={videoRef} key={active.id} src={active.video_url} controls className="w-full h-full"
                data-testid="lesson-video"
                onLoadedMetadata={() => { const p = progMap[active.id]; if (p?.position_sec && videoRef.current) videoRef.current.currentTime = p.position_sec; }}
                onPause={() => saveProgress(false)} onEnded={() => { saveProgress(true); toast.success("Lesson completed!"); }} />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-white/70">
                <Lock className="h-10 w-10 mb-2" /><p>{active ? "Enroll to watch this lesson" : "Select a lesson"}</p>
              </div>
            )}
          </div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900 mt-5">{disp.title}</h1>
          <div className="flex items-center gap-3 mt-2">{course.is_free ? <Badge tone="green">Free Course</Badge> : <Badge tone="indigo">₹{course.selling_price}</Badge>}<span className="text-sm text-slate-500">by {course.created_by_name}</span></div>
          <p className="text-slate-600 mt-3">{disp.description}</p>
          {active && <div className="mt-4 p-4 bg-white rounded-xl border border-slate-200"><h3 className="font-semibold text-slate-800">{active.title}</h3><p className="text-sm text-slate-500 mt-1">{active.content || "Watch the video and mark as complete to track your progress."}</p></div>}
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            {!enrolled && !course.is_free && (
              <button onClick={enroll} disabled={busy} data-testid="buy-course-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-3 rounded-xl mb-3">Buy for ₹{course.selling_price}</button>
            )}
            {!enrolled && course.is_free && (
              <button onClick={enroll} disabled={busy} data-testid="enroll-free-btn" className="w-full bg-teal-700 hover:bg-teal-800 text-white font-bold py-3 rounded-xl mb-3">{busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Enroll for Free"}</button>
            )}
            {enrolled && <div className="mb-3"><div className="flex justify-between text-sm mb-1"><span className="font-semibold text-slate-700">Progress</span><span className="text-teal-700 font-bold">{prog?.course_percent || 0}%</span></div><div className="h-2 bg-slate-100 rounded-full"><div className="h-2 bg-teal-600 rounded-full" style={{ width: `${prog?.course_percent || 0}%` }} /></div></div>}
            <h3 className="font-display font-bold text-slate-900 mb-2">Course Content</h3>
            <p className="text-xs text-slate-500 mb-3">{lessons.length} lessons</p>
            {course.modules.map((m) => (
              <div key={m.id} className="mb-3">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1">{m.title}</p>
                {m.lessons.map((l) => {
                  const done = progMap[l.id]?.completed;
                  return (
                    <button key={l.id} onClick={() => selectLesson(l)} data-testid={`lesson-${l.id}`} className={`w-full flex items-center gap-2 text-left px-2 py-2 rounded-lg text-sm ${active?.id === l.id ? "bg-indigo-50 text-indigo-800" : "hover:bg-slate-50 text-slate-700"}`}>
                      {done ? <CheckCircle2 className="h-4 w-4 text-teal-600 shrink-0" /> : l.lesson_type === "video" ? <PlayCircle className="h-4 w-4 shrink-0" /> : <FileText className="h-4 w-4 shrink-0" />}
                      <span className="flex-1 truncate">{l.title}</span>
                      {!canPlay(l) && <Lock className="h-3.5 w-3.5 text-slate-400" />}
                      {l.is_preview && !enrolled && <Badge tone="green">Preview</Badge>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
